import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { DatePicker as DefaultDatePicker } from '@united-talent-agency/components';
import dateToAmPm from '../../../utils/dateToAmPm';
import dateToTime from '../../../utils/dateToTime';
import dateToDateString from '../../../utils/dateToDateString';
import stringAndFormatToDate from '../../../utils/stringAndFormatToDate';
import getLocaleFormat from '../../../utils/getLocaleFormat';

function isValidDate(date) {
  return date instanceof Date && !isNaN(date);
}

export default function DatePicker({
  open,
  focusPicker,
  occurrenceDate,
  initWithCurrentDate,
  dateLabelCypressTag,
  dateButtonCypressTag,
  // Functions
  onBlur,
  onFocus,
  onClose,
  onChange,
}) {
  let locale = navigator.language;

  // Restricts locale usage to English variants only (what getLocaleFormat supports);
  // other locales are replaced with "en-US".
  if (!/^en/g.test(locale)) {
    locale = 'en-US';
  }

  const initialDate = useMemo(
    () => (occurrenceDate ? new Date(occurrenceDate) : undefined),
    [occurrenceDate]
  );

  // Refs
  const dateTime = useRef(initialDate);
  const dateStringRef = useRef(isValidDate(initialDate) ? dateToDateString(initialDate) : '');
  const timeStringRef = useRef(isValidDate(initialDate) ? dateToTime(initialDate, true) : '');
  const twelveHourStringRef = useRef(isValidDate(initialDate) ? dateToAmPm(initialDate) : '');

  // States
  const [dateString, setDateString] = useState(
    isValidDate(initialDate) ? dateToDateString(initialDate) : ''
  );
  const [timeString, setTimeString] = useState(
    isValidDate(initialDate) ? dateToTime(initialDate, true) : ''
  );
  const [twelveHourString, setTwelveHourString] = useState(
    isValidDate(initialDate) ? dateToAmPm(initialDate) : ''
  );

  const dateToInputStates = useCallback(
    (date) => {
      if (!isValidDate(date)) {
        dateStringRef.current = '';
        timeStringRef.current = '';
        twelveHourStringRef.current = '';
        setDateString('');
        setTimeString('');
        setTwelveHourString('');
        return;
      }

      const newTimeDisplay = date.toLocaleString(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      });

      const newDateString = newTimeDisplay.split(' ')[0].slice(0, -1);
      const newTimeString = newTimeDisplay.split(' ')[1];
      const newTwelveHourPeriodString = newTimeDisplay.split(' ')[2].toUpperCase();

      dateStringRef.current = newDateString;
      timeStringRef.current = newTimeString;
      twelveHourStringRef.current = newTwelveHourPeriodString;
      setDateString(newDateString);
      setTimeString(newTimeString);
      setTwelveHourString(newTwelveHourPeriodString);
    },
    [locale]
  );

  const handleDatetimeChange = useCallback(() => {
    const dateStr = `${dateStringRef.current}, ${timeStringRef.current} ${twelveHourStringRef.current}`;
    const date = stringAndFormatToDate(getLocaleFormat(true, true, true), dateStr);

    if (date) {
      dateTime.current = date;
      onChange && onChange(date);
    }
  }, [onChange]);

  useEffect(() => {
    const dateStr = `${dateStringRef.current}, ${timeStringRef.current} ${twelveHourStringRef.current}`;
    const date = stringAndFormatToDate(getLocaleFormat(true, true, true), dateStr);

    if (initialDate?.getTime?.() === date?.getTime?.()) {
      return;
    }

    dateToInputStates(initialDate);
  }, [dateToInputStates, initialDate, onChange]);

  const handleOnChangeTime = (time) => {
    setTimeString(time);

    if (initWithCurrentDate && !occurrenceDate) {
      const now = new Date();
      dateToInputStates(now);
    }

    if (!time || time.length < 4) {
      return;
    }

    timeStringRef.current = time;
    handleDatetimeChange();
  };

  const handleOnChangeTwelveHourPeriod = (period) => {
    setTwelveHourString(period);

    if (!period) {
      return;
    }

    twelveHourStringRef.current = period;
    handleDatetimeChange();
  };

  const handleOnChange = (newDate) => {
    if (initWithCurrentDate && !occurrenceDate) {
      const now = new Date();
      dateToInputStates(now);
    }

    setDateString(newDate);

    if (!newDate) {
      return;
    }

    dateStringRef.current = newDate;
    handleDatetimeChange();
  };

  const handleOnBlur = (e) => {
    onClose && onClose(dateTime.current);
    onBlur && onBlur(e);
  };

  const handleOnFocus = (e) => {
    if (initWithCurrentDate && !occurrenceDate) {
      const now = new Date();
      dateToInputStates(now);
      onChange && onChange(now);
    }

    onFocus && onFocus(e);
  };

  return (
    <DefaultDatePicker
      timeIncluded
      inputFieldReadOnly
      open={open}
      focusPicker={focusPicker}
      dateString={dateString}
      timeString={timeString}
      twelveHourPeriod={twelveHourString}
      dateLabelCypressTag={dateLabelCypressTag}
      dateButtonCypressTag={dateButtonCypressTag}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
      onChange={handleOnChange}
      onChangeTime={handleOnChangeTime}
      onChangeTwelveHourPeriod={handleOnChangeTwelveHourPeriod}
    />
  );
}
